import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  Button,
  DateInput,
  DateTimeTz,
  Field,
  Header,
  Input,
  Label,
  OrderCard,
  SelectInput,
  Tooltip,
  useAlert,
} from "@web-app/components";
import { Order, RoadieQuoteResponse } from "@web-app/types";
import http from "@web-app/utils/http";

type PackageSize = "large" | "medium" | "small" | "xlarge";

const PACKAGE_SIZE_OPTIONS: {
  label: string;
  value: PackageSize;
}[] = [
  { label: "Small (eg. envelope, water bottle)", value: "small" },
  { label: "Medium (eg. retail bag, shoe box)", value: "medium" },
  { label: "Large (eg. computer monitor, microwave)", value: "large" },
  { label: "X-Large (eg. 42 inch flat screen TV)", value: "xlarge" },
];

const PACKAGE_SIZE_DIMENSIONS: Record<
  PackageSize,
  { height: number; length: number; weight: number; width: number }
> = {
  large: { height: 24, length: 36, weight: 50, width: 24 },
  medium: { height: 18, length: 24, weight: 30, width: 18 },
  small: { height: 6, length: 12, weight: 10, width: 10 },
  xlarge: { height: 36, length: 48, weight: 100, width: 36 },
};

const ITEM_DESCRIPTION_MAX = 500;

const orderItemSchema = z.object({
  description: z
    .string({ required_error: "Description is required" })
    .trim()
    .min(1, "Description is required")
    .max(
      ITEM_DESCRIPTION_MAX,
      `Description cannot exceed ${ITEM_DESCRIPTION_MAX} characters`,
    ),
  quantity: z
    .number({ required_error: "Quantity is required" })
    .int("Quantity must be a whole number")
    .min(1, "Quantity must be at least 1"),
  scheduledEndDateTime: z.string().datetime(),
  scheduledStartDateTime: z.string().datetime(),
  size: z.enum(["small", "medium", "large", "xlarge"], {
    required_error: "Package size is required",
  }),
  value: z
    .number({ required_error: "Value is required" })
    .min(1, "Value must be at least $1"),
});

const roadieFormSchema = z.object({
  isContainAlcohol: z.enum(["yes", "no"], {
    required_error: "Alcohol information is required",
  }),
  orderItems: z.record(orderItemSchema),
  pickupLocationType: z.enum(["other", "food_service_establishment"], {
    required_error: "Pickup location type is required",
  }),
});

type OrderItemDetails = z.infer<typeof orderItemSchema>;
type RoadieFormData = z.infer<typeof roadieFormSchema>;

const NYC_FOOD_ESTABLISHMENT_LAW =
  "NYC Local Law 115/2021: " +
  "NYC law defines a food establishment as a place where food is provided for individual portion service " +
  "directly to the consumer, whether the food is provided free of charge or sold, and whether the food " +
  "is consumed on or off the premises. This includes restaurants, employee cafeterias, bakeries, takeouts, " +
  "pizzerias, night clubs, cabarets, bars, senior centers, emergency food relief organizations, public " +
  "and non-public schools, or religious, fraternal and charitable organizations. For these purposes, " +
  "if a customer has a food service establishment permit, they are covered by the law.";

export function OrderRoadieDetails({
  onClose,
  entity,
  entityId,
  orders,
}: {
  entity: "order" | "route";
  entityId: string;
  onClose: () => void;
  orders: Order[];
}) {
  const { showAlertAsync } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [roadieQuotation, setRoadieQuotation] =
    useState<RoadieQuoteResponse | null>(null);
  const [isQuotationFetched, setIsQuotationFetched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<RoadieFormData>({
    defaultValues: {
      isContainAlcohol: "no",
      orderItems: (() => {
        const items: RoadieFormData["orderItems"] = {};

        const buildItem = (order: Order): OrderItemDetails => ({
          description: order.details?.description ?? "",
          quantity: 1,
          scheduledEndDateTime: new Date(
            order.scheduledEndDateTime,
          ).toISOString(),
          scheduledStartDateTime: new Date(
            order.scheduledStartDateTime,
          ).toISOString(),
          size: "medium",
          value: 1,
        });

        if (entity === "order") {
          items[entityId] = buildItem(orders[0]);
        } else if (entity === "route" && orders) {
          orders.forEach((order) => {
            items[order.id] = buildItem(order);
          });
        }

        return items;
      })(),
      pickupLocationType: "other",
    },
    mode: "onChange",
    resolver: zodResolver(roadieFormSchema),
  });

  const getRoadieQuotation = async (formData: RoadieFormData) => {
    if (entity === "order") {
      // can only request quote for p2p orders
      setIsLoading(true);
      setServerError(null);

      const orderItem = formData.orderItems[entityId];
      const dims = PACKAGE_SIZE_DIMENSIONS[orderItem.size];
      const quoteRequestPayload = {
        description: orderItem.description,
        height: dims.height,
        is_contain_alcohol: formData.isContainAlcohol,
        length: dims.length,
        pickup_location_type: formData.pickupLocationType,
        quantity: orderItem.quantity,
        scheduled_end: orderItem.scheduledEndDateTime,
        scheduled_start: orderItem.scheduledStartDateTime,
        value: orderItem.value,
        weight: dims.weight,
        width: dims.width,
      };

      const data = await showAlertAsync(
        http.post(
          `/integrations/roadie/quote?orderId=${entityId}`,
          quoteRequestPayload,
        ),
        {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          error: (err: any) => {
            setIsLoading(false);
            if (err instanceof AxiosError) {
              const errMsg = `${err.response?.data?.message}`;
              setServerError(errMsg.trim());
            }
            return {
              message: "Failed to fetch Roadie quotation",
              title: "Error",
              type: "error",
            };
          },
          loading: {
            message: "Fetching Roadie quotation...",
            title: "Fetching",
            type: "default",
          },
          success: {
            message: "Roadie quotation fetched",
            title: "Success",
            type: "success",
          },
        },
      ).then((response) => response.data);

      setRoadieQuotation(data);
      setIsQuotationFetched(true);
      setIsLoading(false);
    }
  };

  const handleCreateShipment = async () => {
    setIsLoading(true);
    setServerError(null);
    const formData = getValues();

    const shipmentItems: Record<
      string,
      {
        description: string;
        height: number;
        length: number;
        quantity: number;
        scheduled_end: string;
        scheduled_start: string;
        value: number;
        weight: number;
        width: number;
      }
    > = {};

    Object.keys(formData.orderItems).forEach((orderId) => {
      const item = formData.orderItems[orderId];
      const dims = PACKAGE_SIZE_DIMENSIONS[item.size];
      shipmentItems[orderId] = {
        description: item.description,
        height: dims.height,
        length: dims.length,
        quantity: item.quantity,
        scheduled_end: item.scheduledEndDateTime,
        scheduled_start: item.scheduledStartDateTime,
        value: item.value,
        weight: dims.weight,
        width: dims.width,
      };
    });

    const payload = {
      is_contain_alcohol: formData.isContainAlcohol,
      items: shipmentItems,
      pickup_location_type: formData.pickupLocationType,
    };

    await showAlertAsync(
      http.post(`/integrations/roadie/${entity}/${entityId}/shipment`, payload),
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (err: any) => {
          setIsLoading(false);
          if (err instanceof AxiosError) {
            const errMsg = `${err.response?.data?.message}`;
            setServerError(errMsg.trim());
          }
          return {
            message: "Failed to send order to Roadie",
            title: "Error",
            type: "error",
          };
        },
        loading: {
          message: "Sending order to Roadie...",
          title: "Sending",
          type: "default",
        },
        success: () => {
          onClose();
          return {
            message: "Order sent to Roadie",
            title: "Success",
            type: "success",
          };
        },
      },
    );
  };

  return (
    <div className="absolute bg-white h-full w-full z-30 flex flex-col">
      {/* Sticky Header */}
      <Header
        title={
          entity === "route" ? "Send Route to Roadie" : "Send Order to Roadie"
        }
        leftActions={
          <Button
            icon="fasChevronLeft"
            onClick={onClose}
            plain
            className="!p-0 hover:!bg-white"
          />
        }
      />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {(() => {
          const ordersWithMissingStreet = orders.filter(
            (order) =>
              (order.pickup && !order.pickup.streetAddress) ||
              (order.dropoff && !order.dropoff.streetAddress),
          );
          if (ordersWithMissingStreet.length === 0) return null;
          const formatOrderLabel = (order: Order) =>
            order.externalId
              ? `${order.externalId} (${order.displayId})`
              : order.displayId;
          return (
            <div className="w-full bg-yellow-50 border border-yellow-200 rounded-md p-3 mx-4 mt-4 mb-4">
              <div className="text-sm font-medium text-yellow-800">
                The following orders are missing street address information for
                pickup or dropoff:{" "}
                {ordersWithMissingStreet.map(formatOrderLabel).join(", ")}. This
                may cause issues when sending to Roadie.
              </div>
            </div>
          );
        })()}

        {serverError && (
          <div className="w-full bg-white rounded-md space-y-2 mx-4 mb-4">
            <div className="text-sm font-medium text-red-500">
              {serverError}
            </div>
          </div>
        )}

        {isQuotationFetched &&
          (() => {
            const formData = getValues();
            const orderItem = formData.orderItems[entityId];
            const sizeLabel = PACKAGE_SIZE_OPTIONS.find(
              (opt) => opt.value === orderItem?.size,
            )?.label;

            return (
              <>
                {/* Quote Response */}
                <div className="flex flex-col gap-2 px-4 pb-4 [&:not(:last-child)]:border-b border-gray-200">
                  <div className="grid pb-2">
                    <span className="tw-details-field-title font-semibold">
                      Roadie Quote
                    </span>
                  </div>
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    <span className="tw-details-field-title">Cost</span>
                    <span className="tw-details-field-value font-semibold">
                      {`$${(roadieQuotation?.price ?? 0).toFixed(2)}`}
                    </span>
                  </div>
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    <span className="tw-details-field-title">Distance</span>
                    <span className="tw-details-field-value">
                      {`${(roadieQuotation?.estimated_distance ?? 0).toFixed(2)} miles`}
                    </span>
                  </div>
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    <span className="tw-details-field-title">Size</span>
                    <span className="tw-details-field-value">
                      {roadieQuotation?.size}
                    </span>
                  </div>
                </div>

                {/* Shipment Details */}
                {orderItem && (
                  <div className="flex flex-col gap-2 px-4 py-4 [&:not(:last-child)]:border-b border-gray-200">
                    <div className="grid pb-2">
                      <span className="tw-details-field-title font-semibold">
                        Shipment Details
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_2fr] gap-4">
                      <span className="tw-details-field-title">
                        Package Size
                      </span>
                      <span className="tw-details-field-value">
                        {sizeLabel}
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_2fr] gap-4">
                      <span className="tw-details-field-title">
                        Description
                      </span>
                      <span className="tw-details-field-value break-words">
                        {orderItem.description || "N/A"}
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_2fr] gap-4">
                      <span className="tw-details-field-title">Value</span>
                      <span className="tw-details-field-value">
                        {`$${orderItem.value}`}
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_2fr] gap-4">
                      <span className="tw-details-field-title">Quantity</span>
                      <span className="tw-details-field-value">
                        {orderItem.quantity}
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_2fr] gap-4">
                      <span className="tw-details-field-title">
                        Pickup After
                      </span>
                      <span className="tw-details-field-value">
                        <DateTimeTz
                          start={new Date(orderItem.scheduledStartDateTime)}
                          timezone={orders[0].timezone}
                          presentation="default"
                        />
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_2fr] gap-4">
                      <span className="tw-details-field-title">Deliver By</span>
                      <span className="tw-details-field-value">
                        <DateTimeTz
                          start={new Date(orderItem.scheduledEndDateTime)}
                          timezone={orders[0].timezone}
                          presentation="default"
                        />
                      </span>
                    </div>
                  </div>
                )}

                {/* Order Card */}
                <div className="px-4 py-4">
                  <OrderCard order={orders[0]} />
                </div>
              </>
            );
          })()}

        {!isQuotationFetched && (
          <div className="w-full bg-white rounded-md px-4">
            <div className="flex flex-col gap-2">
              <Field className="w-full">
                <Label>
                  Pickup Location Type <span className="text-red-500">*</span>{" "}
                  <Tooltip text={NYC_FOOD_ESTABLISHMENT_LAW} />
                </Label>
                <Controller
                  name="pickupLocationType"
                  control={control}
                  rules={{
                    required: "Pickup location type is required",
                  }}
                  render={({ field }) => (
                    <SelectInput
                      options={[
                        {
                          label: "Other",
                          value: "other",
                        },
                        {
                          label: "Food service establishment",
                          value: "food_service_establishment",
                        },
                      ]}
                      name="pickup_location_type"
                      selected={field.value}
                      onChange={(value: string) => {
                        field.onChange(value);
                      }}
                      required
                    />
                  )}
                />
              </Field>

              <Field className="w-full">
                <Label>
                  Does it Contain Alcohol?{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="isContainAlcohol"
                  control={control}
                  rules={{
                    required: "Alcohol information is required",
                  }}
                  render={({ field }) => (
                    <SelectInput
                      options={[
                        {
                          label: "Yes",
                          value: "yes",
                        },
                        {
                          label: "No",
                          value: "no",
                        },
                      ]}
                      name="deliverable_action"
                      selected={field.value}
                      onChange={(value: string) => {
                        field.onChange(value);
                      }}
                      required
                    />
                  )}
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-4 mt-4">
              {entity === "route" ? (
                orders.map((order) => (
                  <OrderItemFields
                    key={order.id}
                    order={order}
                    control={control}
                    errors={errors}
                    getValues={getValues}
                  />
                ))
              ) : (
                <OrderItemFields
                  key={entityId}
                  order={orders[0]}
                  control={control}
                  errors={errors}
                  getValues={getValues}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Footer */}
      <div className="border-t border-gray-200 p-4 bg-white">
        <div className="flex gap-2">
          {isQuotationFetched && (
            <Tooltip text="Accept quote to send order to Roadie">
              <Button
                type="button"
                isLoading={isLoading}
                onClick={() => handleCreateShipment()}
              >
                Accept Quote
              </Button>
            </Tooltip>
          )}
          {!isQuotationFetched &&
            (entity === "order" ? (
              <Tooltip text="Request quote to see the estimated cost">
                <Button
                  type="button"
                  isLoading={isLoading}
                  onClick={handleSubmit(getRoadieQuotation)}
                >
                  Request Quote
                </Button>
              </Tooltip>
            ) : (
              <Tooltip text="Request quote to see the estimated cost">
                <Button
                  type="button"
                  isLoading={isLoading}
                  onClick={handleSubmit(() => handleCreateShipment())}
                >
                  Create Shipment
                </Button>
              </Tooltip>
            ))}
          <Button type="button" plain onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

function OrderItemFields({
  order,
  control,
  errors,
  getValues,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValues: any;
  order: Order;
}) {
  const orderId = order.id;

  return (
    <div className="w-full bg-gray-50 rounded-lg border border-gray-200 p-4">
      <div className="mb-3">
        <Label className="text-gray-900 font-semibold text-base">
          Order:{" "}
          {order?.externalId
            ? `${order.externalId} (${order.displayId})`
            : order?.displayId}
        </Label>
      </div>

      <div className="flex flex-col gap-3">
        {/* Package Size */}
        <Field className="w-full">
          <Label>
            Package Size <span className="text-red-500">*</span>
          </Label>
          <Controller
            name={`orderItems.${orderId}.size`}
            control={control}
            rules={{
              required: "Package size is required",
            }}
            render={({ field }) => (
              <SelectInput
                options={PACKAGE_SIZE_OPTIONS}
                name="package_size"
                selected={field.value}
                onChange={(value: string) => {
                  field.onChange(value);
                }}
                required
              />
            )}
          />
        </Field>

        {/* Description */}
        <Field className="w-full">
          <Label>
            Description <span className="text-red-500">*</span>
          </Label>
          <Controller
            name={`orderItems.${orderId}.description`}
            control={control}
            rules={{
              required: "Description is required",
            }}
            render={({ field }) => (
              <>
                <Input
                  type="text"
                  placeholder="eg. Electronics, Groceries"
                  value={field.value}
                  onChange={(e) => field.onChange(e)}
                  invalid={!!errors.orderItems?.[orderId]?.description?.message}
                />
                {errors.orderItems?.[orderId]?.description?.message && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.orderItems[orderId].description.message}
                  </p>
                )}
              </>
            )}
          />
        </Field>

        {/* Value & Quantity */}
        <div className="grid grid-cols-2 gap-3">
          <Field className="w-full">
            <Label>
              Value ($) <span className="text-red-500">*</span>
            </Label>
            <Controller
              name={`orderItems.${orderId}.value`}
              control={control}
              rules={{
                min: {
                  message: "Value must be at least $1",
                  value: 1,
                },
                required: "Value is required",
              }}
              render={({ field }) => (
                <>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="1.00"
                    value={field.value}
                    onChange={(e) => field.onChange(parseFloat(e))}
                    invalid={!!errors.orderItems?.[orderId]?.value?.message}
                  />
                  {errors.orderItems?.[orderId]?.value?.message && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.orderItems[orderId].value.message}
                    </p>
                  )}
                </>
              )}
            />
          </Field>

          <Field className="w-full">
            <Label>
              Quantity <span className="text-red-500">*</span>
            </Label>
            <Controller
              name={`orderItems.${orderId}.quantity`}
              control={control}
              rules={{
                min: {
                  message: "Quantity must be at least 1",
                  value: 1,
                },
                required: "Quantity is required",
              }}
              render={({ field }) => (
                <>
                  <Input
                    type="number"
                    step="1"
                    placeholder="1"
                    value={field.value}
                    onChange={(e) => field.onChange(parseInt(e))}
                    invalid={!!errors.orderItems?.[orderId]?.quantity?.message}
                  />
                  {errors.orderItems?.[orderId]?.quantity?.message && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.orderItems[orderId].quantity.message}
                    </p>
                  )}
                </>
              )}
            />
          </Field>
        </div>

        {/* Scheduled Times */}
        <div className="grid grid-cols-2 gap-3">
          <Field className="w-full">
            <Label>
              Pickup After <span className="text-red-500">*</span>
            </Label>
            <Controller
              name={`orderItems.${orderId}.scheduledStartDateTime`}
              control={control}
              rules={{
                required: "Pickup time is required",
              }}
              render={({ field }) => (
                <DateInput
                  value={new Date(field.value)}
                  onChange={(date) => field.onChange(date.toISOString())}
                  timezone={order.timezone}
                />
              )}
            />
          </Field>

          <Field className="w-full">
            <Label>
              Deliver By <span className="text-red-500">*</span>
            </Label>
            <Controller
              name={`orderItems.${orderId}.scheduledEndDateTime`}
              control={control}
              rules={{
                required: "Delivery time is required",
                validate: (value: string) => {
                  const start = new Date(
                    getValues(`orderItems.${orderId}.scheduledStartDateTime`),
                  );
                  const end = new Date(value);
                  const diffMs = end.getTime() - start.getTime();
                  const diffHours = diffMs / (1000 * 60 * 60);
                  if (diffHours < 2) {
                    return "Delivery window must be at least 2 hours after pickup time";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <>
                  <DateInput
                    value={new Date(field.value)}
                    onChange={(date) => field.onChange(date.toISOString())}
                    timezone={order.timezone}
                  />
                  {errors.orderItems?.[orderId]?.scheduledEndDateTime
                    ?.message && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.orderItems[orderId].scheduledEndDateTime.message}
                    </p>
                  )}
                </>
              )}
            />
          </Field>
        </div>
      </div>
    </div>
  );
}
